/**
 * Boot.
 *
 * Building the deck is expensive — procedural textures, a PMREM environment,
 * a few hundred meshes — so the sequence yields a frame between stages and
 * reports progress, rather than freezing on a white screen.
 */

import { lib, refreshAccount } from '../state/library.ts';
import * as player from '../state/player.ts';
import { deck } from '../state/player.ts';
import { prefsSnapshot } from '../state/prefs.ts';
import * as ui from '../state/ui.ts';
import { createStage } from '../three/stage.ts';
import { buildTurntable } from '../three/turntable.ts';
import { HOME_VIEW } from '../three/views.ts';
import { startingCrate } from './browse.ts';
import { installDebugHandle } from './debug.ts';
import { offerFullscreen, wireFullscreen } from './fullscreen.ts';
import { wireGestures } from './gestures.ts';
import { installHardware } from './hardware.ts';
import { loadTrack, messageOf } from './playback.ts';
import { wireShortcuts } from './shortcuts.ts';
import { startRenderLoop, wireDeck, wireVisibility } from './sync.ts';

/**
 * Yield a frame so the browser can paint the progress ring.
 *
 * A backgrounded tab never fires requestAnimationFrame, so the wait is
 * bounded — otherwise the app would sit on the loading screen forever until
 * someone looked at it.
 */
const FRAME_TIMEOUT = 80;

const frame = (): Promise<void> =>
  new Promise((resolve) => {
    let settled = false;
    const done = (): void => {
      if (settled) return;
      settled = true;
      resolve();
    };
    requestAnimationFrame(done);
    setTimeout(done, FRAME_TIMEOUT);
  });

async function stage(progress: number, message: string): Promise<void> {
  ui.setBootProgress(progress);
  ui.setBootMessage(message);
  await frame();
}

/**
 * Unlocks audio on the very first interaction anywhere on the page, and takes
 * itself off once it has. Returns a teardown for the case where the app is
 * disposed before anyone touches it.
 */
function wireAudioUnlock(): () => void {
  const stop = (): void => {
    removeEventListener('pointerdown', unlock);
    removeEventListener('keydown', unlock);
  };
  const unlock = (): void => {
    void deck.demo.ensure().catch(() => undefined);
    stop();
  };
  addEventListener('pointerdown', unlock);
  addEventListener('keydown', unlock);
  return stop;
}

export interface BootResult {
  dispose: () => void;
}

export async function boot(canvas: HTMLCanvasElement): Promise<BootResult> {
  await stage(0.12, 'Warming up the deck');

  const built = createStage(canvas);
  await stage(0.32, 'Machining the platter');

  const tt = buildTurntable(built.renderer);
  built.scene.add(tt.root);
  installHardware({ stage: built, tt });
  await stage(0.62, 'Cutting the grooves');

  built.resize();
  void built.applyView(HOME_VIEW, { animate: false });

  const teardown = [
    wireGestures(),
    wireShortcuts(),
    wireVisibility(),
    wireAudioUnlock(),
    wireFullscreen(),
  ];
  wireDeck();
  await stage(0.82, 'Stacking the crate');

  applyInitialSettings();
  refreshAccount();

  /* Start with the demo crate cued up but silent — nothing plays until asked. */
  const crate = startingCrate();
  const first = crate[0];
  if (first) await loadTrack(first, crate, 0, { autoplay: false, cinematic: false });

  await stage(1, 'Ready');
  const loop = startRenderLoop();

  /*
   * Order matters here. Dropping the loading screen mounts the glass chrome;
   * only then can the camera fit measure it and lift the deck's own controls
   * clear of the top bar and the now-playing bar. `ready` is raised last, so
   * it means "built, wired, and correctly framed".
   */
  ui.setBooting(false);
  await frame();
  built.resize();

  installDebugHandle();
  ui.setReady(true);
  dispatchEvent(new CustomEvent('turntable:ready'));
  offerFullscreen();
  announceDemoMode();

  return {
    dispose() {
      loop.stop();
      for (const off of teardown) off();
      deck.destroy();
      built.dispose();
    },
  };
}

function applyInitialSettings(): void {
  const prefs = prefsSnapshot();
  deck.setVolume(prefs.defaultVolume);
  deck.demo.setCrackle(prefs.crackle);
  player.setVolume(prefs.defaultVolume);
  player.setShuffle(deck.playlist.shuffle);
  player.setRepeat(deck.playlist.repeat);
  player.setRpm(33);
  player.setPitch(0);
}

function announceDemoMode(): void {
  if (lib.configured) return;
  setTimeout(() => {
    ui.toast('Demo mode — tap the pill to connect YouTube Music', 4200);
  }, 1400);
}

/** Reports a boot failure on the loading screen rather than a blank stage. */
export function bootFailed(err: unknown): void {
  console.error(err);
  ui.setBootProgress(1);
  ui.setBootMessage(messageOf(err, 'Failed to start.'));
}
